'use client'

import { css } from '@lastbrand/web-ui-styled-system/css'
import React from 'react'

import { Link } from '@src/quince-components/src/atoms'
import QRCanvas from '@src/quince-components/src/atoms/qrCanvas/QRCanvas'
import { TRACK_PAGE_TYPE } from '@src/quince-site-common/src/domains/PageTypes'
import {
  type AccountPlacement,
  useAppInstallAccountsPresenter,
} from '@src/quince-site-common/src/organisms/appLayout/molecules/footer/molecules/app-install/app-install.presenter'
import QuinceLogo from '@src/quince-site-common/src/organisms/appLayout/molecules/header/icons/quince-q-logo.svg'

/*
 * DS/Panda twin of `AppInstallAccounts` (the SCSS-modules wishlist banner). The
 * SCSS version cannot be reused on a DS page: legacy sizing is fluid-rem
 * (`html { font-size: 0.6944vw }`), which is disabled under
 * `<html data-styles="panda">`, so its rem values would render at the 16px DS
 * root (~1.6x oversized). This variant expresses the same layout with fixed DS
 * units. Values in px comments refer to the legacy sheet at its ~10px root.
 *
 * Like the SCSS variant, copy arrives via props (i18n) rather than the
 * presenter's Statsig copy; the presenter supplies only behavior/URLs.
 */

interface AppInstallAccountsDesktopConfig {
  title: string
  qrAlt: string
}

interface AppInstallAccountsMobileConfig {
  title: string
  actionLabel: string
}

interface AppInstallAccountsDsProps {
  trackPageType?: TRACK_PAGE_TYPE
  placement: AccountPlacement
  desktopConfig: AppInstallAccountsDesktopConfig
  mobileConfig: AppInstallAccountsMobileConfig
}

export const appInstallAccountsWrapperCss = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  // STRFRNT-17121: the QR and its caption had NO gap and sat flush. Nothing else was supplying the
  // separation either — measured on the running wishlist page, the canvas resolves to
  // `display: block` (so no inline-baseline descender slack) and its bitmap is drawn edge-to-edge
  // (`margin={0}`, confirmed by sampling the canvas pixels for a white quiet zone — there is none),
  // leaving canvas-bottom to caption-top at exactly 0. This gap is therefore the only thing that
  // separates them.
  //
  // `space-4` is the Figma `QrCode` component's own value (`7143:129418`, `gap: space/4`). The
  // ticket text asks for 2px; design confirmed the Figma node is the source of truth here, so the
  // 2px in the ticket is the outlier and worth confirming with the reporter before it is closed.
  gap: 'space-4',
  // Legacy: 1rem / 120% / 500 caption under the QR.
  fontSize: '10px',
  lineHeight: '120%',
  fontWeight: 500,
})

const appInstallAccountsWrapperMobileCss = css({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  background: 'paperwhite',
  paddingBlock: 'space-12',
  paddingInline: 'space-16',
  width: 'full',
  border: '1px solid token(colors.lightgray)',
  borderRadius: '0.4rem',
})

const appInstallAccountsTitleMobileCss = css({
  fontSize: '14px',
  lineHeight: '140%',
  fontWeight: 500,
  color: 'black',
  marginLeft: 'space-12',
  marginRight: 'space-8',
})

const appInstallAccountsLinkMobileCss = css({
  fontSize: '10px',
  lineHeight: '120%',
  fontWeight: 500,
  background: 'black',
  color: 'white',
  flexShrink: 0,
  paddingBlock: 'space-10',
  paddingInline: '14px',
  borderRadius: '20px',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
})

const appInstallAccountsLogoMobileCss = css({ flexShrink: 0 })

export const AppInstallAccountsDs = ({
  trackPageType,
  placement,
  desktopConfig,
  mobileConfig,
}: AppInstallAccountsDsProps) => {
  const p = useAppInstallAccountsPresenter({ trackPageType, placement })

  if (!p.shouldRender) return null

  if (p.isMobile) {
    return (
      <section aria-label={mobileConfig.title} className={appInstallAccountsWrapperMobileCss}>
        <QuinceLogo aria-hidden="true" width={48} height={48} className={appInstallAccountsLogoMobileCss} />
        <div className={appInstallAccountsTitleMobileCss}>{mobileConfig.title}</div>
        <Link href={p.mwebDeeplink} onClick={p.onCtaClick} className={appInstallAccountsLinkMobileCss}>
          {mobileConfig.actionLabel}
        </Link>
      </section>
    )
  }

  return (
    <section aria-label={desktopConfig.title} className={appInstallAccountsWrapperCss}>
      {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
      <div role="img" aria-label={desktopConfig.qrAlt}>
        <QRCanvas url={p.dwebDeeplink} widthInPx={64} margin={0} />
      </div>
      <div>{desktopConfig.title}</div>
    </section>
  )
}
